import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../features/authSlice';
import { getTokenExpirationTime } from '../utils/jwtUtils';

/**
 * Hook to refresh JWT token based on mouse activity
 * Refreshes token when user moves mouse and token is approaching expiration
 */
export const useActivityTokenRefresh = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const lastRefreshTime = useRef(0);
  const isRefreshing = useRef(false);

  const refreshToken = useCallback(async () => {
    if (!token || isRefreshing.current) return;

    const expirationTime = getTokenExpirationTime(token);
    const timeRemainingSeconds = expirationTime / 1000;

    // Only refresh if less than 10 minutes remain (for 2-hour token)
    // Refresh when less than ~8% of token lifetime remains
    const shouldRefresh = timeRemainingSeconds > 0 && timeRemainingSeconds < 600; // Less than 10 minutes

    if (!shouldRefresh) return;

    // Prevent multiple refresh requests (minimum 5 seconds between refreshes)
    const now = Date.now();
    if (now - lastRefreshTime.current < 5000) {
      return;
    }

    try {
      isRefreshing.current = true;
      lastRefreshTime.current = now;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          dispatch(login(data.token));
        }
      }
    } catch (error) {
      // Silent error handling
    } finally {
      isRefreshing.current = false;
    }
  }, [token, dispatch]);

  useEffect(() => {
    if (!token) return;

    const handleMouseMove = () => {
      refreshToken();
    };

    // Add mouse move listener
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [token, refreshToken]);
};
